// src/pages/merchant-pages/OrgShippedContainers.js
import { useState, useEffect, useContext, forwardRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Utils
import { toMoney } from "../../utils/toMoney";
import useLanguage from "../../utils/useLanguage";
import { DataContext } from "../../utils/DataContext";
import { UserAuth } from "../../utils/AuthContext";
import { formatDate } from "../../utils/toDate";

// Assets
import invoicesIcon from "../../assets/invoices.png";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import addIconLight from "../../assets/add_icon_light.png";
import searchIcon from "../../assets/search_icon.png";

// Components
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import HotDropdown from "../../components/HotDropdown";
import CustomButton from "../../components/CustomButton";
import CustomLink from "../../components/CustomLink";
import InformationBox from "../../components/InformationBox";
import UserSection from "../../components/UserSection";
import BackToDashboard from "../../components/BackToDashboard";
import Loader from "../../components/Loader";

// Page sections
import AddFullShippedContainerModal from "./AddFullShippedContainerModal";
import { sanitizeNumber } from "../../utils/sanitizeNumber";

const OrgShippedContainers = forwardRef((props, ref) => {
  const { t } = useTranslation("general");

  const rows = [
    {
      title: "containerNumber",
      value: "containerNumber",
    },
    {
      title: "customer",
      value: "customer",
    },
    {
      title: "abstractAmountForCustomer",
      value: "abstractAmountForCustomer",
    },
    {
      title: "currency",
      value: "currency",
    },
    {
      title: "finalAmountForCustomer",
      value: "finalAmountForCustomer",
    },
    {
      title: "currency",
      value: "currency",
    },
    {
      title: "supplier",
      value: "supplier",
    },
    {
      title: "material",
      value: "material",
    },
    {
      title: "numberOfBoxes",
      value: "numberOfBoxes",
    },
    {
      title: "measuringUnit",
      value: "measuringUnit",
    },
    {
      title: "finalDestination",
      value: "finalDestination",
    },
    {
      title: "expectedArrivalDate",
      value: "expectedArrivalDate",
    },

    {
      title: "shipper",
      value: "shipper",
    },
    {
      title: "exitDate",
      value: "exitDate",
    },
    {
      title: "dateOfEntry",
      value: "dateOfEntry",
    },
    {
      title: "howWasPapersReceived",
      value: "howWasPapersReceived",
    },
    {
      title: "shippingDate",
      value: "shippingDate",
    },
    {
      title: "permitsReceivingDate",
      value: "permitsReceivingDate",
    },
    {
      title: "company",
      value: "subCompany",
    },
    {
      title: "createdBy",
      value: "createdBy",
    },
    {
      title: "notes",
      value: "notes",
    },
  ];

  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("containerNumber"),
    value: "containerNumber",
  });
  const [searchQuery, setSearchQuery] = useState("");

  const { currentDirection } = useLanguage();

  const [showAddContainerModal, setShowAddContainerModal] = useState(false);
  const [showAddInvoiceButton, setShowAddInvoiceButton] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchByCustomer, setSearchByCustomer] = useState(null);
  const [searchBySupplier, setSearchBySupplier] = useState(null);
  const [howWasPapersReceivedStatus, setHowWasPapersReceivedStatus] =
    useState(null);

  const { userData } = useContext(UserAuth);

  const {
    selectedSupplier,
    setError,
    loading,
    setLoading,
    shippedContainers,
    setShippedContainers,
    getCustomers,
    getSuppliers,
    selectedCustomer,
    suppliers,
    customers,
    setCustomers,
    currentBalance,
    handleSelectCustomer,
    fetchShippedContainers,
    fetchArchivedShippedContainers,
    selectedDate,
    setSelectedDate,
    setNetworkError,
    searchError,
    searchErrorMessage,
    shippedContainersCurrentPage,
    shippedContainersCount,
    shippedContainersTotalPages,
    setShippedContainersCurrentPage,
    subCompanies,
    selectedSubCompany,
    getSubCompanies,
    handleSelectSubCompany,

    selectedShippedContainersStatus,
    setSelectedShippedContainersStatus,
    shippedContainersBalances: balances,
    setShippedContainersBalances: setBalances,
  } = useContext(DataContext);

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
  };

  const handleSearchByCustomer = (customer) => {
    setSearchByCustomer(customer);
  };

  const handleSearchBySupplier = (supplier) => {
    setSearchBySupplier(supplier);
  };
  const handleSearchByPaperReceiver = (howWasPapersReceived) => {
    setHowWasPapersReceivedStatus(howWasPapersReceived);
  };

  const getEmployees = async () => {
    const token =
      localStorage.getItem("SuperAdminToken") ||
      localStorage.getItem("adminToken");

    const organizationId = userData?.organizationId;

    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getEmployees`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": organizationId,
          },
        }
      );
      if (response?.status === 200) {
        setLoading(false);
      }

      if (response.data && response.data.employees) {
        const transformedResults = response.data.employees.map((emp) => {
          return {
            ...emp,
            name: emp?.username ? emp?.username : null,
          };
        });

        setEmployees(transformedResults);
      } else {
        console.error("Failed to get the users");
      }
    } catch (error) {
      setLoading(false);
      setNetworkError(true);
      console.error("Failed to get the users", error);
    }
  };

  // * Reset to default
  useEffect(() => {
    return () => {
      setSelectedShippedContainersStatus({
        title: "Active",
        value: "active",
      });
    };
  }, []);

  useEffect(() => {
    let req;

    if (selectedShippedContainersStatus?.value === "active") {
      req = async () => {
        const response = await fetchShippedContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          selectedSubCompany?.id
        );
        setBalances(response?.balance?._sum);
      };
    } else {
      req = async () => {
        const response = await fetchArchivedShippedContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          selectedSubCompany?.id
        );
        setBalances(response?.balance?._sum);
      };
    }
    req();
  }, [shippedContainersCurrentPage]);

  const [selectAll, setSelectAll] = useState(false);
  const [searchBy, setSearchBy] = useState("");

  const handleShowChart = () => {
    setChartModalOpen(true);
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
  };

  const searchByMoneyV2 = [
    "numberOfBoxes",
    "abstractAmountForCustomer",
    "finalAmountForCustomer",
  ];

  const handleSearch = async (query = searchQuery) => {
    if (typeof query !== "string") {
      query = searchQuery;
    }

    console.log("test", searchBy);
    if (searchByMoneyV2.includes(selectedTitle.value)) {
      query = parseFloat(sanitizeNumber(query));
    }
    console.log("searchQuery", query);

    if (selectedShippedContainersStatus?.title === "Active") {
      await fetchShippedContainers(
        selectedSupplier?.id,
        selectedCustomer?.id,
        selectedSubCompany?.id,
        selectedTitle.value,
        query ||
          selectedDate ||
          selectedEmployee?.id ||
          searchByCustomer?.id ||
          searchBySupplier?.id ||
          howWasPapersReceivedStatus?.value
      );
    } else {
      await fetchArchivedShippedContainers(
        selectedSupplier?.id,
        selectedCustomer?.id,
        selectedSubCompany?.id,
        selectedTitle.value,
        query ||
          selectedDate ||
          selectedEmployee?.id ||
          searchByCustomer?.id ||
          searchBySupplier?.id ||
          howWasPapersReceivedStatus?.value
      );
    }
  };

  const handleShowInputFields = () => {
    setShowAddContainerModal(true);
    // navigate("/createShippedContainerPage");
  };

  const handleSearchType = async (type) => {
    setSelectedTitle(type);
    setSelectedDate("");
    setSearchByCustomer(null);
    setSearchBySupplier(null);
    setHowWasPapersReceivedStatus(null);
    // setSelectedEmployee(null);

    setSearchQuery("");
    setSearchBy("");
    if (
      type.value === "expectedArrivalDate" ||
      type.value === "exitDate" ||
      type.value === "dateOfEntry" ||
      type.value === "shippingDate" ||
      type.value === "permitsReceivingDate"
    ) {
      setSearchBy("date");
    }

    if (type.value === "customer") {
      setSearchBy("customer");
      if (!customers.length) {
        await getCustomers();
      }
    }

    if (type.value === "supplier") {
      setSearchBy("supplier");

      if (!suppliers.length) {
        await getSuppliers();
      }
    }

    if (type.value === "howWasPapersReceived") {
      setSearchBy("howWasPapersReceived");

      // if (!customers.length) {
      //   await getCustomers();
      // }
    }

    if (type.value === "subCompany") {
      setSearchBy("subCompany");
      await getSubCompanies();
    }

    if (type.value === "createdBy") {
      setSearchBy("createdBy");
      await getEmployees();
    }
  };

  const handleChangeStatus = (status) => {
    const { value } = status;
    setSelectedShippedContainersStatus(status);
    setShippedContainersCurrentPage(1);
    setSearchQuery("");

    if (value === "active") {
      setShowAddInvoiceButton(true);
      const req = async () => {
        const response = await fetchShippedContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          selectedSubCompany?.id
        );
        setBalances(response?.balance?._sum);
      };
      req();
    } else if (value === "archived") {
      setShowAddInvoiceButton(false);
      fetchArchivedShippedContainers(
        selectedSupplier?.id,
        selectedCustomer?.id,
        selectedSubCompany?.id
      );
    }
  };

  // start print

  const [analyzeLoading, setAnalyzeLoading] = useState(false);

  const [isAnalyzable, setIsAnalyzable] = useState(false);

  // end print

  const balanceStyle =
    currentBalance?.value >= 0 ? "positive-value" : "negative-value";

  const hiddenBalances =
    selectedCustomer?.id === "All" && selectedSupplier?.id === "All";

  return (
    <>
      <main className="e-container">
        <BackToDashboard
          title={t("back")}
          variant="blank"
          icon={currentDirection === "ltr" ? previousIcon : nextIcon}
          iconSize={14}
          to={`/${userData?.organizationType?.toLowerCase()}-dashboard`}
        />
        <div className="invoice-table-container">
          <UserSection
            handleShowChart={handleShowChart}
            page="shippedContainers"
          />

          <section className="o-row o-row--vertical-align_center u-margin--bottom_300">
            <label className="u-min-width-specific_50">{t("customer")}: </label>

            {selectedCustomer?.id ? (
              <HotDropdown
                initOptions={[{ id: "All", name: "All" }, ...customers]}
                setOptions={setCustomers}
                selected={selectedCustomer}
                setSelected={handleSelectCustomer}
                errorText="Failed to get"
                optionsName={"customers"}
                getData={getCustomers}
              />
            ) : (
              // CUSTOMERS drop down: replaced

              <span>{t("noCustomersFound")}</span>
            )}
          </section>

          <div className="under-user-section">
            <div className="page-title">{t("loadingAndUnloading")}</div>
            <CustomButton
              elementType={Link}
              title={t("invoicesPage")}
              to="/invoicesOfShippedContainers"
              icon={invoicesIcon}
            />
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              {!selectedSupplier?.id ? (
                <div className="full-w-button-container">
                  <div className="column-flex-box flex-center-center">
                    <InformationBox>
                      {t("noSuppliersAddFromUser")}
                    </InformationBox>
                    <CustomButton
                      title={t("goToSuppliersPage")}
                      minWidth={140}
                      variant="blank"
                      elementType={Link}
                      to={"/viewSuppliersPage"}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="top-action-buttons responsive-box">
                    <div className="button-group">
                      {showAddInvoiceButton ? (
                        <>
                          <CustomButton
                            title={t("addContainer")}
                            onClick={handleShowInputFields}
                            icon={addIconLight}
                            iconSize={20}
                          />
                        </>
                      ) : null}
                      {/* {data.length !== 0 && !showInputFields ? (
                          <CustomButton
                            title={t("print")}
                            onClick={handlePrint}
                            variant="gray"
                            icon={printIcon}
                            iconSize={18}
                          />
                        ) : null} */}
                    </div>

                    <section className="col title-col ">
                      <label>{t("invoicesStatus")}: </label>
                      <Dropdown
                        options={[
                          { title: "Active", value: "active" },
                          { title: "Archived", value: "archived" },
                        ]}
                        selected={selectedShippedContainersStatus}
                        setSelected={handleChangeStatus}
                        thick
                      />
                    </section>
                  </div>

                  {shippedContainers?.length !== 0 ? (
                    <>
                      <div className="c-search-bar u-margin--bottom_500">
                        <section className="c-search-bar-label-input">
                          <label>{t("search")}: </label>

                          {searchBy === "date" ? (
                            <DatePicker
                              selected={selectedDate}
                              onChange={(date) => {
                                setSelectedDate(
                                  date.toLocaleDateString("en-CA")
                                );
                              }}
                              dateFormat="yyyy-MM-dd"
                              placeholderText={t("searchByDate")}
                            />
                          ) : searchBy === "subCompany" ? (
                            <>
                              {subCompanies.length ? (
                                <Dropdown
                                  options={subCompanies}
                                  selected={selectedSubCompany}
                                  setSelected={handleSelectSubCompany}
                                  thick
                                  nameInAr={true}
                                />
                              ) : (
                                <span className="error-text">
                                  {t("noSubCompaniesFound")}
                                </span>
                              )}
                            </>
                          ) : searchBy === "customer" ? (
                            <>
                              {customers?.length ? (
                                <Dropdown
                                  options={customers}
                                  selected={searchByCustomer}
                                  setSelected={handleSearchByCustomer}
                                  thick
                                />
                              ) : (
                                <span className="error-text">
                                  {t("noCustomersFound")}
                                </span>
                              )}
                            </>
                          ) : searchBy === "supplier" ? (
                            <>
                              {suppliers?.length ? (
                                <Dropdown
                                  options={suppliers}
                                  selected={searchBySupplier}
                                  setSelected={handleSearchBySupplier}
                                  thick
                                />
                              ) : (
                                <span className="error-text">
                                  {t("noSuppliers")}
                                </span>
                              )}
                            </>
                          ) : searchBy === "howWasPapersReceived" ? (
                            <Dropdown
                              options={[
                                {
                                  title: "receivedByUs",
                                  value: "receivedByUs",
                                },
                                {
                                  title: "receivedByCustomer",
                                  value: "receivedByCustomer",
                                },
                              ]}
                              selected={howWasPapersReceivedStatus}
                              setSelected={handleSearchByPaperReceiver}
                              flex
                              thick
                            />
                          ) : searchBy === "createdBy" ? (
                            <>
                              <Dropdown
                                options={employees}
                                selected={selectedEmployee}
                                setSelected={handleSelectEmployee}
                                thick
                              />
                            </>
                          ) : searchBy === "numberOfBoxes" ||
                            searchBy === "abstractAmountForCustomer" ||
                            searchBy === "finalAmountForCustomer" ? (
                            <CustomInput
                              placeholder={t("search")}
                              value={searchQuery}
                              onChange={(e) => {
                                setSearchQuery(parseFloat(e.target.value));
                              }}
                              resetSearch={() => {
                                setSearchQuery("");
                                handleSearch("");
                              }}
                              thick
                              type="moneyV2"
                            />
                          ) : (
                            <CustomInput
                              placeholder={t("search")}
                              value={searchQuery}
                              onChange={(e) => {
                                setSearchQuery(e.target.value);
                              }}
                              resetSearch={() => {
                                setSearchQuery("");
                                handleSearch("");
                              }}
                              thick
                            />
                          )}
                        </section>

                        <section className="c-search-bar-label-input">
                          <label>{t("searchTitle")}: </label>
                          <Dropdown
                            options={rows?.filter(
                              (row) =>
                                row?.title !== "Customer" &&
                                row?.title !== "Supplier" &&
                                row?.title !== "currency" &&
                                row?.title !== "company"
                            )}
                            selected={selectedTitle}
                            setSelected={handleSearchType}
                            thick
                          />
                        </section>
                        <CustomButton
                          title={t("search")}
                          onClick={handleSearch}
                          icon={searchIcon}
                          iconSize={15}
                        />
                      </div>

                      <section className="table-container">
                        <table className="i-table">
                          <thead className="table-head">
                            <tr>
                              <th className="bold-text">
                                {/* Select or deselect all */}
                                <CustomCheckbox
                                  checked={selectAll}
                                  onChange={handleSelectAll}
                                  color="primary"
                                />
                              </th>

                              {rows.map((row) => {
                                if (
                                  row?.title !== "company" ||
                                  selectedSubCompany?.id === "All"
                                ) {
                                  return (
                                    <th
                                      key={row?.title}
                                      className={`bold-text cell-title ${
                                        row?.value
                                      } ${
                                        row?.value !== "containerNumber"
                                          ? "mobile-hider"
                                          : ""
                                      }`}
                                    >
                                      {t(row?.title)}
                                    </th>
                                  );
                                }
                              })}
                            </tr>
                          </thead>

                          <tbody className="table-body">
                            {/* These are the existing rows */}
                            {shippedContainers?.map(
                              (row, index) =>
                                !row?.canceled && (
                                  <tr key={index}>
                                    <td
                                      className={`cell ${
                                        currentDirection === "ltr"
                                          ? "rounded-first-cell"
                                          : "rounded-last-cell"
                                      }`}
                                    >
                                      <CustomCheckbox
                                        checked={row?.selected || false}
                                        onChange={(e) => {
                                          const updatedRow = [
                                            ...shippedContainers,
                                          ];
                                          updatedRow[index].selected =
                                            e.target.checked;
                                          setShippedContainers(updatedRow);
                                        }}
                                      />
                                    </td>

                                    <td className="cell invoice-number">
                                      {row?.archived ? (
                                        <p className="archived-invoice">
                                          {row?.containerNumber}
                                        </p>
                                      ) : (
                                        <CustomLink
                                          to={`/shippedContainer/${row?.id}`}
                                        >
                                          <p className="link">
                                            {row?.containerNumber}
                                          </p>
                                          {row?.updateCounter > 0 &&
                                            !row?.archived && (
                                              <p className="update-note">
                                                {t("containerHasBeenUpdated")}
                                              </p>
                                            )}
                                        </CustomLink>
                                      )}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.customer?.name}
                                      {/* {customers.find(
                                            (customer) =>
                                              customer.id === row?.customerId
                                          )?.name || "-"} */}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {toMoney(
                                        row?.abstractAmountForCustomer
                                      ) || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.customer?.currency || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {toMoney(row?.finalAmountForCustomer) ||
                                        "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.customer?.currency || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.supplier?.name || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.material || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.numberOfBoxes || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.measuringUnit || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.finalDestination || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.expectedArrivalDate
                                        ? formatDate(row?.expectedArrivalDate)
                                        : "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.shipper || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.exitDate
                                        ? formatDate(row?.exitDate)
                                        : "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.dateOfEntry
                                        ? formatDate(row?.dateOfEntry)
                                        : "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {t(row?.howWasPapersReceived) || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.shippingDate
                                        ? formatDate(row?.shippingDate)
                                        : "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.permitsReceivingDate
                                        ? formatDate(row?.permitsReceivingDate)
                                        : "-"}
                                    </td>

                                    {selectedSubCompany?.id === "All" && (
                                      <td className="cell mobile-hider ">
                                        {row?.subCompany?.nameInAr || "-"}
                                      </td>
                                    )}

                                    <td className="cell mobile-hider">
                                      {row?.user?.name ||
                                        row?.user?.username ||
                                        "-"}
                                    </td>

                                    <td
                                      className={`cell notes-cell mobile-hider ${
                                        currentDirection === "ltr"
                                          ? "rounded-last-cell"
                                          : "rounded-first-cell"
                                      }`}
                                    >
                                      {row?.notes || "-"}
                                    </td>
                                  </tr>
                                )
                            )}
                          </tbody>
                        </table>
                      </section>

                      <section className="pagination">
                        <div className="pagination-buttons">
                          <CustomButton
                            variant="blank"
                            title={t("previous")}
                            onClick={() =>
                              setShippedContainersCurrentPage(
                                shippedContainersCurrentPage - 1
                              )
                            }
                            disabled={shippedContainersCurrentPage === 1}
                            icon={
                              currentDirection === "ltr"
                                ? previousIcon
                                : nextIcon
                            }
                            iconSize={15}
                          />
                          <span className="pagination-location">
                            {t("page")}: {shippedContainersCurrentPage} -{" "}
                            {shippedContainersTotalPages}
                          </span>
                          <CustomButton
                            title={t("next")}
                            variant="blank"
                            onClick={() =>
                              setShippedContainersCurrentPage(
                                shippedContainersCurrentPage + 1
                              )
                            }
                            disabled={
                              shippedContainersCurrentPage ===
                              shippedContainersTotalPages
                            }
                            icon={
                              currentDirection === "ltr"
                                ? nextIcon
                                : previousIcon
                            }
                            iconSize={15}
                            reverseIcon
                          />
                        </div>

                        <div className="pagination-buttons">
                          <span className="pagination-location">
                            {t("totalRecords")}: {shippedContainersCount}
                          </span>
                        </div>
                      </section>

                      {!hiddenBalances && selectedSupplier?.id && (
                        <div className="balances margin-inline o-column o-column--vertical-align_end o-column--gap-size_400 u-margin--top_400">
                          <div className="balance">
                            <span>{t("abstractCurrentBalance")}: </span>
                            <span className={balanceStyle}>
                              {!selectedCustomer?.currency ? (
                                t("noCustomerSelected")
                              ) : (
                                <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                  <span>
                                    {toMoney(
                                      balances?.abstractAmountForCustomer
                                    )}
                                  </span>
                                  <span>{selectedCustomer?.currency}</span>
                                </div>
                              )}
                            </span>
                          </div>
                          <div className="balance">
                            <span>{t("customerCurrentBalance")}: </span>
                            <span className={balanceStyle}>
                              {!selectedCustomer?.currency ? (
                                t("noCustomerSelected")
                              ) : (
                                <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                  <span>
                                    {toMoney(balances?.finalAmountForCustomer)}
                                  </span>
                                  <span>{selectedCustomer?.currency}</span>
                                </div>
                              )}
                            </span>
                          </div>
                          {/* <p className="balance">
                            <span>{t("supplierCurrentBalance")}: </span>
                            <span className={balanceStyle}>
                              {!selectedSupplier?.currency ||
                              selectedSupplier?.currency === "N/A" ? (
                                t("noSupplierSelected")
                              ) : (
                                <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                  <span>
                                    {toMoney(balances?.amountForSupplier)}
                                  </span>
                                  <span>{selectedSupplier?.currency}</span>
                                </div>
                              )}
                            </span>
                          </p> */}
                        </div>
                      )}
                    </>
                  ) : (
                    <InformationBox>
                      {searchError ? t(searchErrorMessage) : t("noInvoices")}
                    </InformationBox>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Render the ChartModal component */}
      {/* <ChartModal
        open={chartModalOpen}
        onClose={handleCloseChart}
        data={data}
      /> */}

      {showAddContainerModal && (
        <AddFullShippedContainerModal
          setShowAddContainerModal={setShowAddContainerModal}
        />
      )}
    </>
  );
});

export default OrgShippedContainers;
